import { isDev } from "./is-dev";

/**
 * Whether ARIA lets an element have an author-given name (`aria-label`), from
 * the role it renders with. ARIA 1.2 prohibits naming roles such as generic,
 * presentation, paragraph, strong, emphasis, code, deletion, insertion,
 * subscript and superscript, so `aria-label` there is invalid.
 *
 * The role that renders: an explicit `role` (a string, even one that removes a
 * mapped role by being `undefined`), else the element's implicit role. Elements
 * whose implicit role depends on their ancestors (`header`, `footer`, `li`,
 * `figcaption`) are treated as not nameable: this can't see ancestors, so it
 * never emits a prohibited name for them; give them an explicit role instead.
 */

/** Roles whose name isn't prohibited (ARIA 1.2). */
const NAMEABLE_ROLES = new Set([
  "alert",
  "alertdialog",
  "application",
  "article",
  "banner",
  "blockquote",
  "button",
  "cell",
  "checkbox",
  "columnheader",
  "combobox",
  "complementary",
  "contentinfo",
  "dialog",
  "document",
  "feed",
  "figure",
  "form",
  "grid",
  "gridcell",
  "group",
  "heading",
  "img",
  "link",
  "list",
  "listbox",
  "listitem",
  "log",
  "main",
  "marquee",
  "math",
  "menu",
  "menubar",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "meter",
  "navigation",
  "note",
  "option",
  "progressbar",
  "radio",
  "radiogroup",
  "region",
  "row",
  "rowgroup",
  "rowheader",
  "scrollbar",
  "search",
  "searchbox",
  "separator",
  "slider",
  "spinbutton",
  "status",
  "switch",
  "tab",
  "table",
  "tablist",
  "tabpanel",
  "textbox",
  "timer",
  "toolbar",
  "tooltip",
  "tree",
  "treegrid",
  "treeitem",
]);

/**
 * Implicit roles that allow a name, for the elements Box and Text render. An
 * element missing here is generic, has no role, or depends on its ancestors.
 */
const NAMEABLE_ELEMENTS: Record<string, string> = {
  // A name makes a <section> a region
  section: "region",
  article: "article",
  aside: "complementary",
  main: "main",
  nav: "navigation",
  figure: "figure",
  ul: "list",
  address: "group",
  blockquote: "blockquote",
  h1: "heading",
  h2: "heading",
  h3: "heading",
  h4: "heading",
  h5: "heading",
  h6: "heading",
};

/**
 * @param role the role that renders: a string from a role map or an explicit
 *   `role`; `undefined` or `null` when the element keeps its implicit role
 * @param element the tag name
 * @param hasHref for `a`, which is a link only with an `href`
 */
export function canHaveName(
  role: string | null | undefined,
  element: string,
  hasHref = false,
): boolean {
  if (role) return NAMEABLE_ROLES.has(role);
  if (element === "a") return hasHref;
  return element in NAMEABLE_ELEMENTS;
}

const warned = new Set<string>();

/**
 * The `aria-label` to render: the label when the role allows a name, else
 * `undefined`, with a one-time dev warning naming the component and role.
 */
export function ariaLabelFor(
  component: string,
  label: string | undefined,
  role: string | null | undefined,
  element: string,
  hasHref = false,
): string | undefined {
  if (label === undefined) return undefined;
  if (canHaveName(role, element, hasHref)) return label;
  const key = `${component}:${element}:${role ?? ""}`;
  if (isDev() && !warned.has(key)) {
    warned.add(key);
    console.warn(
      `${component}: accessibilityLabel is ignored on a <${element}>${role ? ` with role "${role}"` : ""}, which ARIA doesn't let have a name. Give it a role that can have one, or put the label on an element that can (e.g. a surrounding Box with accessibilityRole="group").`,
    );
  }
  return undefined;
}
